// สร้างการ์ดรูปภาพจากชีวิตที่จบไป แล้วแชร์หรือบันทึกรูป (docs/PLAN.md เฟส 4)

import { useEffect, useState } from "react";
import type { LifeState } from "../../engine/types";
import { placeName } from "../../geo/place";
import { realmOf, type SpeciesRange } from "../../geo/range";
import { shareCardData } from "../../shared/share-card";
import type { GeoState } from "../geo";
import { renderShareCard } from "../share-card";

interface Props {
  life: LifeState;
  geo: GeoState;
  range: SpeciesRange | null;
  achievements: readonly string[]; // ชื่อความสำเร็จที่ได้จากชีวิตนี้
  shareUrl: string;
}

type State = { status: "idle" } | { status: "busy" } | { status: "ready"; blob: Blob; url: string } | { status: "error"; message: string };

export function ShareCardPanel({ life, geo, range, achievements, shareUrl }: Props) {
  const [state, setState] = useState<State>({ status: "idle" });
  const [note, setNote] = useState<string | null>(null);
  const fileName = `wild-lifepack-${life.species.sci.toLowerCase().replace(/[^a-z]+/g, "-")}.png`;

  // คืนหน่วยความจำของรูปเก่าเมื่อสร้างใหม่หรือออกจากหน้า
  useEffect(() => () => {
    if (state.status === "ready") URL.revokeObjectURL(state.url);
  }, [state]);

  async function create() {
    setState({ status: "busy" });
    setNote(null);
    try {
      const ready = geo.status === "ready" && life.geo ? geo.data : null;
      const places = ready && life.geo
        ? { born: placeName(ready.grid, life.geo.birth.cell, life.geo.birth.onSea, life.geo.path[0]), died: placeName(ready.grid, life.geo.cell, life.geo.onSea, life.geo.at) }
        : { born: null, died: null };
      const data = shareCardData(life, places, achievements);
      const map = ready && life.geo ? { world: ready.world, range, path: life.geo.path, realm: realmOf(life.species.traits) } : null;
      const blob = await renderShareCard(data, map, location.host);
      setState({ status: "ready", blob, url: URL.createObjectURL(blob) });
    } catch (e) {
      setState({ status: "error", message: (e as Error).message });
    }
  }

  async function share(blob: Blob) {
    const file = new File([blob], fileName, { type: "image/png" });
    try {
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: `ลองเกิดเป็นสัตว์ตัวเดียวกับฉันที่ ${shareUrl}` });
        return;
      }
      setNote("เบราว์เซอร์นี้แชร์รูปตรงๆ ไม่ได้ กด \"บันทึกรูป\" แล้วแชร์จากเครื่องแทนได้เลย");
    } catch (e) {
      if ((e as Error).name !== "AbortError") setNote("แชร์ไม่สำเร็จ ลองบันทึกรูปแล้วแชร์เองแทน");
    }
  }

  if (state.status !== "ready") {
    return (
      <div className="share-card">
        <button className="btn block" onClick={create} disabled={state.status === "busy"}>
          {state.status === "busy" ? "กำลังสร้างรูป…" : "สร้างการ์ดรูปภาพสำหรับแชร์"}
        </button>
        {state.status === "error" && <p className="error small">{state.message}</p>}
      </div>
    );
  }

  return (
    <section className="card share-card">
      <img src={state.url} alt={`การ์ดสรุปชีวิตของ${life.species.nameTh ?? life.species.sci}`} width={1080} height={1350} />
      <div className="actions">
        <button className="btn primary block" onClick={() => share(state.blob)}>
          แชร์รูปนี้
        </button>
        <a className="btn block" href={state.url} download={fileName}>
          บันทึกรูป
        </a>
      </div>
      {note && <p className="small muted">{note}</p>}
    </section>
  );
}
